import React, { useState, useEffect, useRef } from 'react';
import { optimizeUnsplashUrl, generateSrcSet, getBlurDataUrl } from '../utils/image-utils';

export interface OptimizedImageProps {
    src: string;
    alt: string;
    width: number;
    height: number;
    className?: string;
    sizes?: string;
    priority?: boolean;
    objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    quality?: number;
    onLoad?: () => void;
    onError?: () => void;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * OptimizedImage Component
 * Automatically optimizes images with WebP format, lazy loading, and responsive sizes
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    alt,
    width,
    height,
    className = '',
    sizes = '100vw',
    priority = false,
    objectFit = 'cover',
    quality = 80,
    onLoad,
    onError,
    onClick
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isInView, setIsInView] = useState(priority); // If priority, load immediately
    const imgRef = useRef<HTMLImageElement>(null);

    // Lazy loading with Intersection Observer
    useEffect(() => {
        if (priority || !imgRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '50px', // Start loading 50px before image enters viewport
                threshold: 0.01
            }
        );

        observer.observe(imgRef.current);

        return () => {
            observer.disconnect();
        };
    }, [priority]);

    // Optimize image URL
    const optimizedSrc = src.includes('unsplash.com')
        ? optimizeUnsplashUrl(src, { width, quality })
        : src;

    // Generate srcset for responsive images
    const srcSet = src.includes('unsplash.com')
        ? generateSrcSet(src, [400, 800, 1200, 1600])
        : undefined;

    // Handle image load
    const handleLoad = () => {
        setIsLoaded(true);
        onLoad?.();
    };

    // Handle image error
    const handleError = () => {
        setHasError(true);
        onError?.();
    };

    // Blur placeholder
    const blurDataUrl = getBlurDataUrl(width, height);

    // Object fit class
    const objectFitClass = {
        cover: 'object-cover',
        contain: 'object-contain',
        fill: 'object-fill',
        none: 'object-none',
        'scale-down': 'object-scale-down'
    }[objectFit];

    return (
        <div
            ref={imgRef}
            className={`relative overflow-hidden ${className}`}
            style={{
                width: '100%',
                aspectRatio: `${width} / ${height}`
            }}
            onClick={onClick}
        >
            {/* Blur Placeholder */}
            {!isLoaded && !hasError && (
                <img
                    src={blurDataUrl}
                    alt=""
                    aria-hidden="true"
                    className={`absolute inset-0 w-full h-full ${objectFitClass} blur-sm scale-110`}
                />
            )}

            {/* Main Image */}
            {isInView && !hasError && (
                <img
                    src={optimizedSrc}
                    srcSet={srcSet}
                    sizes={sizes}
                    alt={alt}
                    width={width}
                    height={height}
                    loading={priority ? 'eager' : 'lazy'}
                    decoding="async"
                    onLoad={handleLoad}
                    onError={handleError}
                    className={`
            w-full h-full ${objectFitClass}
            transition-opacity duration-300
            ${isLoaded ? 'opacity-100' : 'opacity-0'}
            ${className}
          `}
                />
            )}

            {/* Error State */}
            {hasError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="text-center text-gray-400">
                        <svg
                            className="w-12 h-12 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <p className="text-xs">Image not available</p>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {!isLoaded && !hasError && isInView && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-brand-lime border-t-transparent rounded-full animate-spin" />
                </div>
            )}
        </div>
    );
};

/**
 * OptimizedAvatar Component
 * Specialized component for circular avatar images
 */
export const OptimizedAvatar: React.FC<Omit<OptimizedImageProps, 'objectFit'>> = (props) => {
    return (
        <div
            className="rounded-full overflow-hidden flex-shrink-0"
            style={{
                width: props.width || 48,
                height: props.height || 48,
                minWidth: props.width || 48,
                minHeight: props.height || 48
            }}
        >
            <OptimizedImage {...props} objectFit="cover" />
        </div>
    );
};
